import { useEffect, useState } from 'react'

import type { ParticipantMedia } from '@livekit/components-core'
import { observeParticipantMedia } from '@livekit/components-core'
import type { TrackPublication, LocalParticipant, Room } from 'livekit-client'

import { useEnsureRoom } from '@/src/context'

/** @public */
export interface UseLocalParticipantOptions {
  /**
   * The room to use. If not provided, the hook will use the room from the context.
   */
  room?: Room
}

/**
 * The `useLocalParticipant` hook returns the local participant and the associated state
 * around the participant.
 *
 * @example
 * ```tsx
 * const { localParticipant } = useLocalParticipant();
 * ```
 * @public
 */
export function useLocalParticipant(options: UseLocalParticipantOptions = {}) {
  const room = useEnsureRoom(options.room)
  const [localParticipant, setLocalParticipant] = useState(room.localParticipant)
  const [isMicrophoneEnabled, setIsMicrophoneEnabled] = useState(
    localParticipant.isMicrophoneEnabled,
  )
  const [isCameraEnabled, setIsCameraEnabled] = useState(localParticipant.isMicrophoneEnabled)
  const [lastMicrophoneError, setLastMicrophoneError] = useState(
    localParticipant.lastMicrophoneError,
  )
  const [lastCameraError, setLastCameraError] = useState(localParticipant.lastCameraError)
  const [isScreenShareEnabled, setIsScreenShareEnabled] = useState(
    localParticipant.isMicrophoneEnabled,
  )
  const [microphoneTrack, setMicrophoneTrack] = useState<TrackPublication | undefined>(undefined)
  const [cameraTrack, setCameraTrack] = useState<TrackPublication | undefined>(undefined)

  const handleUpdate = (media: ParticipantMedia<LocalParticipant>) => {
    setIsCameraEnabled(media.isCameraEnabled)
    setIsMicrophoneEnabled(media.isMicrophoneEnabled)
    setIsScreenShareEnabled(media.isScreenShareEnabled)
    setCameraTrack(media.cameraTrack)
    setMicrophoneTrack(media.microphoneTrack)
    setLastMicrophoneError(media.participant.lastMicrophoneError)
    setLastCameraError(media.participant.lastCameraError)
    setLocalParticipant(media.participant)
  }
  useEffect(() => {
    const listener = observeParticipantMedia(room.localParticipant).subscribe(handleUpdate)
    // TODO also listen to permission and metadata etc. events
    return () => listener.unsubscribe()
  }, [room])

  return {
    isMicrophoneEnabled,
    isScreenShareEnabled,
    isCameraEnabled,
    microphoneTrack,
    cameraTrack,
    lastMicrophoneError,
    lastCameraError,
    localParticipant,
  }
}
